import cn from 'classnames'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { ComposedReportPlanning } from 'legacy/lut/components/widgets/public/partner360/planning/ComposedReportPlanning'
import { IPlanningRef } from 'legacy/lut/components/widgets/public/partner360/planning/models/IPlanningRef'
import {
  applyFilter,
  selectFilterIsEmpty,
  selectIsFilterApplied,
} from 'legacy/lut/features/partner360/composedReport'
import { IComposedFilterForReport } from 'legacy/lut/features/partner360/composedReport/models/state'
import { selectComposedReportFYear } from 'legacy/lut/features/partner360/composedReport/selectors/selectComposedReportFYear'
import {
  setAvailableActualsTypes,
  setAvailableCurrencies,
} from 'legacy/lut/features/partner360/configuration'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { LoaderOverlay } from 'core-components/library/loader'
import MeasureController from 'core/analytics/MeasureController'
import { guid } from 'core/classes/helpers/GuidHelper'
import {
  DEFAULT_NETNDP_PARTNER_USER_TYPES,
  DEFAULT_NETNDP_TYPES,
} from 'partner360/models/actualValueTypes'

import { useUserRoles } from '../../../../../hooks/useUserRoles'

import { DnALoadDate } from './components/dnaLoadDate/DnALoadDate'
import { FilterArea } from './components/filterArea/FilterArea'
import { createRequestsForOnFilter } from './helpers/createRequestsForOnFilter'

import styles from './ComposedReport.module.scss'

interface IProps {
  metrics: MeasureController
  widgetParams: IObjectAny
}

export const ComposedReport = ({ metrics, widgetParams }: IProps) => {
  const dispatch = useDispatch()

  const [lastGuid, setLastGuid] = useState<string | null>(null)
  const [isFiltersChanged, setIsFiltersChanged] = useState(false)
  const [isFiltersLoading, setIsFiltersLoading] = useState(false)
  const [dnaLoadDate, setDnALoadDate] = useState<string | null>(null)
  const planningRef = useRef<IPlanningRef>(null)

  const composedReportFYear = useSelector(selectComposedReportFYear)
  const isEmptyFilter = useSelector(selectFilterIsEmpty)
  const isFilterApplied = useSelector(selectIsFilterApplied)

  const { isPartnerUser } = useUserRoles()

  const onFilter = useCallback(
    async (filterForReport: IComposedFilterForReport, notFetchPartnersInScope: boolean) => {
      const lastGuid = guid()
      const dataForProcedures = { ...filterForReport, Guid: lastGuid }
      setLastGuid(lastGuid)
      setIsFiltersLoading(true)

      const [scopeRes, dnaLoadDateRes] = await Promise.all(
        createRequestsForOnFilter(dataForProcedures, notFetchPartnersInScope, composedReportFYear)
      )
      setIsFiltersLoading(false)
      setDnALoadDate(dnaLoadDateRes)
      if (planningRef.current) {
        await planningRef.current.updatePlanning(JSON.stringify(dataForProcedures))
        dispatch(applyFilter({ newScope: scopeRes, appliedFilter: filterForReport }))
      }
    },
    [dispatch, composedReportFYear]
  )

  useEffect(() => {
    const actualTypeOptions = isPartnerUser
      ? DEFAULT_NETNDP_PARTNER_USER_TYPES
      : DEFAULT_NETNDP_TYPES
    // init actualsTypes for composed report
    dispatch(setAvailableActualsTypes(actualTypeOptions))
    // init currencies for composed report
    dispatch(setAvailableCurrencies(['USD']))
    return () => {
      dispatch(setAvailableActualsTypes([]))
      dispatch(setAvailableCurrencies([]))
    }
  }, [dispatch, isPartnerUser])

  return (
    <div>
      <LoaderOverlay show={isFiltersLoading} />
      <FilterArea
        metrics={metrics}
        onFilter={onFilter}
        lastGuid={lastGuid}
        setIsFiltersChanged={setIsFiltersChanged}
      />

      <div className={cn({ [styles.blurred]: isFiltersChanged })}>
        <RenderWhen condition={isFilterApplied}>
          <DnALoadDate dnaLoadDate={dnaLoadDate} />
        </RenderWhen>

        <RenderWhen condition={!isEmptyFilter}>
          <ComposedReportPlanning
            metrics={metrics}
            pid={''}
            ref={planningRef}
            widgetParams={widgetParams}
          />
        </RenderWhen>
      </div>
    </div>
  )
}

export default ComposedReport
